import styled, { css } from 'styled-components';

import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    recipesInputText,
    styledInteraction,
    styledTypography,
} from '@pkg/primitives';

import { type FieldTextSize, type FieldTextTheme } from './FieldText.types';

import { tokens } from './FieldText.tokens';

interface StyledFieldTextProps {
    $prefixOffset?: number;
    $suffixOffset?: number;
    $size?: FieldTextSize;
    $theme?: FieldTextTheme;
    $backdropBlur?: boolean;
    $hasInput?: boolean;
    $tabularNums?: boolean;
}

interface AttachedProps {
    $passthrough?: boolean;
}

const paddingX = css`
    padding-left: calc(var(--field-text-padding) + var(--field-text-prefix-offset));
    padding-right: calc(var(--field-text-padding) + var(--field-text-suffix-offset));
`;

const labelMinimized = css`
    --field-text-label-opacity: 0.3;
    --field-text-label-scale: 0.8;
    --field-text-label-translate-y: -1em;
`;

export const StyledFieldText = styled.div<StyledFieldTextProps>`
    --field-text-bg-color: rgb(255, 255, 255, 0.06);
    --field-text-bg-color-subdued: rgb(255, 255, 255, 0.04);
    --field-text-bg-color-hover: rgb(255, 255, 255, 0.1);
    --field-text-bg-color-active: rgb(255, 255, 255, 0.09);

    --field-text-backdrop-filter: ${({ $backdropBlur }) => ($backdropBlur ? 'blur(20px)' : undefined)};

    --field-text-prefix-offset: ${({ $prefixOffset = 0 }) => $prefixOffset}px;
    --field-text-suffix-offset: ${({ $suffixOffset = 0 }) => $suffixOffset}px;

    --field-text-label-opacity: 0.5;
    --field-text-label-scale: 1;
    --field-text-label-translate-y: 0;

    z-index: ${tokens.zindex.base};
    position: relative;
    display: grid;
    color: white;
    // Required by FireFox.
    height: var(--field-text-height);
    // Required to guard against problematic parent styles.
    flex: 1 1 auto;
    text-align: left;

    &:focus-within {
        ${labelMinimized};
    }

    ${({ $hasInput }) => $hasInput && labelMinimized};

    ${({ $size = 'medium' }) => {
        return css`
            --field-text-gap: ${tokens.gap[$size]}px;
            --field-text-padding: ${tokens.padding[$size]}px;
            --field-text-height: ${tokens.height[$size]}px;
            --field-text-font-size: ${tokens.fontSize[$size]}px;
        `;
    }};

    ${({ $theme }) =>
        $theme === 'light' &&
        css`
            --field-text-bg-color: rgb(0, 0, 0, 0.06);
            --field-text-bg-color-subdued: rgb(0, 0, 0, 0.04);
            --field-text-bg-color-hover: rgb(0, 0, 0, 0.1);
            --field-text-bg-color-active: rgb(0, 0, 0, 0.09);
            color: ${designColor.coal};
        `};

    > input {
        ${recipesInputText.basic};
        ${paddingX};

        z-index: ${tokens.zindex.input};
        position: relative;
        grid-area: 1 / 1;
        width: 100%;
        height: var(--field-text-height);

        font-size: var(--field-text-font-size);
        font-weight: 600;
        font-variant-numeric: ${({ $tabularNums }) => ($tabularNums ? 'tabular-nums' : undefined)};
        color: currentColor;
        background-color: var(--field-text-bg-color);
        border-radius: 999px;
        backdrop-filter: var(--field-text-backdrop-filter);

        // FireFox flexible width input
        // These styles are required for the <input /> to shrink.
        position: absolute;
        inset: 0;

        &:disabled,
        &:read-only {
            &,
            &:hover,
            &:active {
                background-color: var(--field-text-bg-color-subdued);
            }
        }

        &:focus,
        &:hover {
            background-color: var(--field-text-bg-color-hover);
        }

        &:active {
            background-color: var(--field-text-bg-color-active);
        }

        &:invalid,
        &[data-input-error] {
            --field-text-bg-color: rgb(255, 0, 76, 0.1);
            --field-text-bg-color-hover: rgb(255, 0, 76, 0.14);
            --field-text-bg-color-active: rgb(255, 0, 76, 0.13);

            outline-color: ${designColor.redDark};
            outline-offset: 0;

            &:focus-visible {
                outline-color: ${designColor.red};
                outline-offset: 2px;
            }
        }
    }
`;

export const LabelWrapper = styled.div`
    ${styledInteraction.interactionDisable};
    ${paddingX};
    z-index: ${tokens.zindex.label};
    position: relative;
    display: grid;
    align-content: center;
    grid-area: 1 / 1;
`;

export const Label = styled.label`
    ${styledTypography.allianceBase};
    ${styledTypography.truncate};
    ${styledTypography.truncateHeightFix()};
    font-size: var(--field-text-font-size);
    font-weight: 700;
    color: currentColor;
    opacity: var(--field-text-label-opacity);
    scale: var(--field-text-label-scale);
    translate: 0 var(--field-text-label-translate-y);
    transform-origin: 0 0;
    transition-property: color, opacity, scale, translate;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};
`;

const attachedStyles = css<AttachedProps>`
    ${({ $passthrough }) => $passthrough && styledInteraction.interactionDisable};
    position: relative;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: var(--field-text-gap);
    grid-area: 1 / 1;
    align-self: center;
`;

export const Prefix = styled.div<AttachedProps>`
    ${attachedStyles};
    z-index: ${tokens.zindex.prefix};
    justify-self: start;
`;

export const Suffix = styled.div<AttachedProps>`
    ${attachedStyles};
    z-index: ${tokens.zindex.suffix};
    justify-self: end;
`;
